import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1770823556202-2eba715a415b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ProjectInquiry() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    project_type: "residential", location: "", scope: "",
    timeline: "", budget_range: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries/project", form);
      setDone(true);
      toast.success(t("inquiry.success"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="inquiry-page">
      <section className="relative bg-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="label-mono text-[#FFB800]">B2B · Construction & Contracting</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4 max-w-3xl">{t("inquiry.title")}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">{t("inquiry.subtitle")}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        {done ? (
          <div className="border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="inquiry-success">
            <Building2 className="w-12 h-12 mx-auto text-emerald-600" />
            <div className="font-display font-black text-2xl mt-4">{t("inquiry.success")}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-5 border border-slate-200 p-6 sm:p-8" data-testid="inquiry-form">
            <Field label={t("inquiry.companyName")} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} required testid="inq-company" />
            <Field label={t("inquiry.contactName")} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required testid="inq-contact" />
            <Field label={t("auth.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="inq-email" />
            <Field label={t("auth.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="inq-phone" />
            <label className="block">
              <div className="label-mono text-slate-500 mb-1.5">{t("inquiry.projectType")}</div>
              <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" data-testid="inq-type">
                {Object.entries(t("inquiry.types")).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <Field label={t("inquiry.location")} value={form.location} onChange={(v) => setForm({ ...form, location: v })} required testid="inq-location" />
            <Field label={t("inquiry.scope")} value={form.scope} onChange={(v) => setForm({ ...form, scope: v })} required full textarea testid="inq-scope" />
            <Field label={t("inquiry.timeline")} value={form.timeline} onChange={(v) => setForm({ ...form, timeline: v })} testid="inq-timeline" />
            <Field label={t("inquiry.budget")} value={form.budget_range} onChange={(v) => setForm({ ...form, budget_range: v })} testid="inq-budget" />
            <div className="md:col-span-2">
              <button type="submit" disabled={loading} className="btn-maroon w-full md:w-auto px-10 py-3.5 font-bold tracking-wide disabled:opacity-60" data-testid="inq-submit">
                {loading ? t("checkout.processing") : t("inquiry.submit")}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, full, textarea, testid }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="label-mono text-slate-500 mb-1.5">{label}{required ? " *" : ""}</div>
      {textarea ? (
        <textarea value={value} required={required} onChange={(e) => onChange(e.target.value)} rows={5} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm resize-none" data-testid={testid} />
      ) : (
        <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" data-testid={testid} />
      )}
    </label>
  );
}
